// src/pages/NomEmpleadosPage.jsx
import React, { useEffect, useRef, useState } from "react";
import PageHeader from "../components/PageHeader";
import TableToolbar from "../components/TableToolbar";
import DataTable from "../components/DataTable";
import Toast, { useToast } from "../components/Toast";
import {
  obtenerEmpleados, obtenerEmpleado, crearEmpleado, actualizarEmpleado,
  obtenerDepartamentos, obtenerPuestos, obtenerRegPatronales,
} from "../services/nominaService";

const EMPTY = {
  Clave: "", Nombre: "", ApellidoPaterno: "", ApellidoMaterno: "",
  Rfc: "", Curp: "", Nss: "",
  FechaIngreso: "", FechaNacimiento: "",
  Sexo: "", EstadoCivil: "",
  Direccion: "", Colonia: "", Ciudad: "", Estado: "", CodigoPostal: "",
  Telefono: "", Email: "",
  DepartamentoId: "", PuestoId: "",
  TipoContrato: "01", TipoJornada: "01",
  SueldoDiario: "", Sdi: "", Sbc: "",
  TipoPago: "03", Clabe: "", NumeroCuenta: "", NombreBanco: "",
  RegPatronalId: "", FrecuenciaPago: "Q",
  Activo: true,
};

const TIPO_CONTRATO = [
  { v: "01", l: "Indefinido" }, { v: "02", l: "Eventual" },
  { v: "03", l: "Por obra" }, { v: "04", l: "Estacional" },
];
const TIPO_JORNADA = [
  { v: "01", l: "Completa" }, { v: "02", l: "Medio tiempo" }, { v: "03", l: "Por horas" },
];
const FRECUENCIA = [
  { v: "S", l: "Semanal" }, { v: "C", l: "Catorcenal" },
  { v: "Q", l: "Quincenal" }, { v: "M", l: "Mensual" },
];
const TIPO_PAGO = [
  { v: "01", l: "Efectivo" }, { v: "02", l: "Cheque" }, { v: "03", l: "Transferencia" },
];

const LABEL_FREC = { S: "Semanal", C: "Catorcenal", Q: "Quincenal", M: "Mensual" };

function Badge({ activo }) {
  return (
    <span style={{
      display: "inline-block", padding: "2px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600,
      background: activo ? "var(--clr-success-light, #dcfce7)" : "#fee2e2",
      color: activo ? "var(--clr-success, #16a34a)" : "#dc2626",
    }}>
      {activo ? "Activo" : "Baja"}
    </span>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--clr-text-muted)", marginBottom: 4 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function Input({ name, value, onChange, type = "text", required, placeholder, readOnly }) {
  return (
    <input
      className="lmd-input"
      type={type}
      name={name}
      value={value ?? ""}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      readOnly={readOnly}
      style={{ width: "100%" }}
    />
  );
}

function Select({ name, value, onChange, options, required }) {
  return (
    <select className="lmd-select" name={name} value={value ?? ""} onChange={onChange} required={required} style={{ width: "100%" }}>
      <option value="">— Seleccionar —</option>
      {options.map((o) => (
        <option key={o.v ?? o.Id} value={o.v ?? o.Id}>{o.l ?? o.Nombre}</option>
      ))}
    </select>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontWeight: 700, fontSize: 13, color: "var(--clr-primary)", borderBottom: "1px solid var(--clr-border)", paddingBottom: 6, marginBottom: 12 }}>
        {title}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "0 16px" }}>
        {children}
      </div>
    </div>
  );
}

export default function NomEmpleadosPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const [deptos, setDeptos] = useState([]);
  const [puestos, setPuestos] = useState([]);
  const [regPat, setRegPat] = useState([]);

  const { toasts, closeToast, success: toastSuccess, error: toastError } = useToast();

  // Ref para disparar recarga manual sin crear dependencias inestables
  const [reloadKey, setReloadKey] = useState(0);
  const reload = () => setReloadKey((k) => k + 1);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    obtenerEmpleados(page, pageSize, search)
      .then((res) => {
        if (!cancelled) { setRows(res.data ?? []); setTotal(res.total ?? 0); }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [page, pageSize, search, reloadKey]);

  useEffect(() => {
    obtenerDepartamentos(false).then(setDeptos).catch(() => {});
    obtenerPuestos(false).then(setPuestos).catch(() => {});
    obtenerRegPatronales().then(setRegPat).catch(() => {});
  }, []);

  const openNew = () => { setEditId(null); setForm(EMPTY); setShowModal(true); };

  const openEdit = async (id) => {
    try {
      const emp = await obtenerEmpleado(id);
      // El API devuelve camelCase; EMPTY y los inputs usan PascalCase en sus `name`
      // Mapeamos aquí una vez para que el formulario funcione correctamente
      setForm({
        Clave: emp.clave ?? "",
        Nombre: emp.nombre ?? "",
        ApellidoPaterno: emp.apellidoPaterno ?? "",
        ApellidoMaterno: emp.apellidoMaterno ?? "",
        Rfc: emp.rfc ?? "",
        Curp: emp.curp ?? "",
        Nss: emp.nss ?? "",
        FechaIngreso: emp.fechaIngreso ? emp.fechaIngreso.slice(0, 10) : "",
        FechaNacimiento: emp.fechaNacimiento ? emp.fechaNacimiento.slice(0, 10) : "",
        Sexo: emp.sexo ?? "",
        EstadoCivil: emp.estadoCivil ?? "",
        Direccion: emp.direccion ?? "",
        Colonia: emp.colonia ?? "",
        Ciudad: emp.ciudad ?? "",
        Estado: emp.estado ?? "",
        CodigoPostal: emp.codigoPostal ?? "",
        Telefono: emp.telefono ?? "",
        Email: emp.email ?? "",
        DepartamentoId: emp.departamentoId ?? "",
        PuestoId: emp.puestoId ?? "",
        TipoContrato: emp.tipoContrato ?? "01",
        TipoJornada: emp.tipoJornada ?? "01",
        SueldoDiario: emp.sueldoDiario ?? "",
        Sdi: emp.sdi ?? "",
        Sbc: emp.sbc ?? "",
        TipoPago: emp.tipoPago ?? "03",
        Clabe: emp.clabe ?? "",
        NumeroCuenta: emp.numeroCuenta ?? "",
        NombreBanco: emp.nombreBanco ?? "",
        RegPatronalId: emp.regPatronalId ?? "",
        FrecuenciaPago: emp.frecuenciaPago ?? "Q",
        Activo: emp.activo ?? true,
      });
      setEditId(id);
      setShowModal(true);
    } catch {
      toastError("Error al cargar empleado");
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      SueldoDiario: parseFloat(form.SueldoDiario) || 0,
      Sdi: parseFloat(form.Sdi) || 0,
      Sbc: parseFloat(form.Sbc) || 0,
      DepartamentoId: form.DepartamentoId ? parseInt(form.DepartamentoId) : null,
      PuestoId: form.PuestoId ? parseInt(form.PuestoId) : null,
      RegPatronalId: form.RegPatronalId ? parseInt(form.RegPatronalId) : null,
      FechaIngreso: form.FechaIngreso || null,
      FechaNacimiento: form.FechaNacimiento || null,
    };
    try {
      if (editId) {
        await actualizarEmpleado(editId, payload);
        toastSuccess("Empleado actualizado");
      } else {
        await crearEmpleado(payload);
        toastSuccess("Empleado creado");
      }
      setShowModal(false);
      reload();
    } catch {
      toastError("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  // El backend devuelve camelCase (ASP.NET Core por defecto)
  const columns = [
    { key: "clave", title: "Clave", style: { width: 90, fontWeight: 600 } },
    { key: "nombreCompleto", title: "Nombre" },
    { key: "rfc", title: "RFC", style: { width: 130 } },
    { key: "nss", title: "NSS", style: { width: 120 } },
    { key: "departamento", title: "Depto." },
    { key: "puesto", title: "Puesto" },
    {
      key: "frecuenciaPago", title: "Frec.",
      render: (r) => LABEL_FREC[r.frecuenciaPago] ?? r.frecuenciaPago ?? "—",
    },
    {
      key: "sueldoDiario", title: "S. Diario",
      render: (r) => `$${parseFloat(r.sueldoDiario || 0).toFixed(2)}`,
      style: { textAlign: "right", width: 100 },
    },
    {
      key: "activo", title: "Estatus",
      render: (r) => <Badge activo={r.activo} />,
      style: { width: 90 },
    },
    {
      key: "_acc", title: "",
      render: (r) => (
        <button className="lmd-btn lmd-btn-xs lmd-btn-outline" onClick={() => openEdit(r.id)}>
          Editar
        </button>
      ),
      style: { width: 70 },
    },
  ];

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div>
      <Toast toasts={toasts} onClose={closeToast} />
      <PageHeader title="Empleados" subtitle="Catálogo de empleados de nómina" />

      <TableToolbar
        searchValue={search}
        onSearchChange={(v) => setSearch(v)}
        onSearch={() => setPage(1)}
        extraRight={
          <button className="lmd-btn lmd-btn-primary" onClick={openNew}>
            + Nuevo empleado
          </button>
        }
      />

      <DataTable columns={columns} rows={rows} keyField="id" loading={loading} />

      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
          <button className="lmd-btn lmd-btn-sm lmd-btn-outline" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
            Anterior
          </button>
          <span style={{ lineHeight: "32px", fontSize: 13 }}>Pág. {page} / {totalPages} ({total} registros)</span>
          <button className="lmd-btn lmd-btn-sm lmd-btn-outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            Siguiente
          </button>
        </div>
      )}

      {/* ===== MODAL ===== */}
      {showModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 900, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 10, padding: 28, width: "95%", maxWidth: 860, maxHeight: "90vh", overflowY: "auto", position: "relative" }}>
            <button onClick={() => setShowModal(false)} style={{ position: "absolute", top: 12, right: 16, border: "none", background: "none", fontSize: 20, cursor: "pointer" }}>✕</button>
            <h3 style={{ margin: "0 0 20px", fontSize: 17 }}>{editId ? "Editar Empleado" : "Nuevo Empleado"}</h3>

            <form onSubmit={handleSubmit}>
              <Section title="Datos generales">
                <Field label="Clave *">
                  <Input name="Clave" value={form.Clave} onChange={handleChange} required placeholder="EMP001" />
                </Field>
                <Field label="Nombre(s) *">
                  <Input name="Nombre" value={form.Nombre} onChange={handleChange} required />
                </Field>
                <Field label="Apellido Paterno *">
                  <Input name="ApellidoPaterno" value={form.ApellidoPaterno} onChange={handleChange} required />
                </Field>
                <Field label="Apellido Materno">
                  <Input name="ApellidoMaterno" value={form.ApellidoMaterno} onChange={handleChange} />
                </Field>
                <Field label="RFC *">
                  <Input name="Rfc" value={form.Rfc} onChange={handleChange} required placeholder="XAXX010101000" />
                </Field>
                <Field label="CURP">
                  <Input name="Curp" value={form.Curp} onChange={handleChange} placeholder="XAXX010101HXXXXXX00" />
                </Field>
                <Field label="NSS (IMSS)">
                  <Input name="Nss" value={form.Nss} onChange={handleChange} placeholder="00000000000" />
                </Field>
                <Field label="Sexo">
                  <Select name="Sexo" value={form.Sexo} onChange={handleChange}
                    options={[{ v: "M", l: "Masculino" }, { v: "F", l: "Femenino" }]} />
                </Field>
                <Field label="Estado civil">
                  <Select name="EstadoCivil" value={form.EstadoCivil} onChange={handleChange}
                    options={[{ v: "S", l: "Soltero/a" }, { v: "C", l: "Casado/a" }, { v: "D", l: "Divorciado/a" }, { v: "V", l: "Viudo/a" }, { v: "U", l: "Unión libre" }]} />
                </Field>
                <Field label="Fecha de nacimiento">
                  <Input name="FechaNacimiento" value={form.FechaNacimiento} onChange={handleChange} type="date" />
                </Field>
              </Section>

              <Section title="Datos laborales">
                <Field label="Fecha de ingreso">
                  <Input name="FechaIngreso" value={form.FechaIngreso} onChange={handleChange} type="date" />
                </Field>
                <Field label="Departamento">
                  <Select name="DepartamentoId" value={form.DepartamentoId} onChange={handleChange}
                    options={deptos.map((d) => ({ v: d.id, l: `${d.clave} — ${d.nombre}` }))} />
                </Field>
                <Field label="Puesto">
                  <Select name="PuestoId" value={form.PuestoId} onChange={handleChange}
                    options={puestos.map((p) => ({ v: p.id, l: `${p.clave} — ${p.nombre}` }))} />
                </Field>
                <Field label="Tipo de contrato">
                  <Select name="TipoContrato" value={form.TipoContrato} onChange={handleChange} options={TIPO_CONTRATO} />
                </Field>
                <Field label="Tipo de jornada">
                  <Select name="TipoJornada" value={form.TipoJornada} onChange={handleChange} options={TIPO_JORNADA} />
                </Field>
                <Field label="Frecuencia de pago">
                  <Select name="FrecuenciaPago" value={form.FrecuenciaPago} onChange={handleChange} options={FRECUENCIA} />
                </Field>
                <Field label="Reg. Patronal IMSS">
                  <Select name="RegPatronalId" value={form.RegPatronalId} onChange={handleChange}
                    options={regPat.map((r) => ({ v: r.id, l: r.numeroRegistro }))} />
                </Field>
              </Section>

              <Section title="Salarios">
                <Field label="Sueldo diario *">
                  <Input name="SueldoDiario" value={form.SueldoDiario} onChange={handleChange} type="number" required placeholder="0.00" />
                </Field>
                <Field label="SDI (Sal. Diario Integrado)">
                  <Input name="Sdi" value={form.Sdi} onChange={handleChange} type="number" placeholder="0.00" />
                </Field>
                <Field label="SBC (Cotización IMSS)">
                  <Input name="Sbc" value={form.Sbc} onChange={handleChange} type="number" placeholder="0.00" />
                </Field>
              </Section>

              <Section title="Datos bancarios">
                <Field label="Forma de pago">
                  <Select name="TipoPago" value={form.TipoPago} onChange={handleChange} options={TIPO_PAGO} />
                </Field>
                <Field label="Banco">
                  <Input name="NombreBanco" value={form.NombreBanco} onChange={handleChange} placeholder="BBVA, BANAMEX..." />
                </Field>
                <Field label="CLABE interbancaria">
                  <Input name="Clabe" value={form.Clabe} onChange={handleChange} placeholder="18 dígitos" />
                </Field>
                <Field label="No. cuenta">
                  <Input name="NumeroCuenta" value={form.NumeroCuenta} onChange={handleChange} />
                </Field>
              </Section>

              <Section title="Contacto">
                <Field label="Teléfono">
                  <Input name="Telefono" value={form.Telefono} onChange={handleChange} />
                </Field>
                <Field label="Email">
                  <Input name="Email" value={form.Email} onChange={handleChange} type="email" />
                </Field>
                <Field label="Dirección">
                  <Input name="Direccion" value={form.Direccion} onChange={handleChange} />
                </Field>
                <Field label="Colonia">
                  <Input name="Colonia" value={form.Colonia} onChange={handleChange} />
                </Field>
                <Field label="Ciudad">
                  <Input name="Ciudad" value={form.Ciudad} onChange={handleChange} />
                </Field>
                <Field label="Estado">
                  <Input name="Estado" value={form.Estado} onChange={handleChange} />
                </Field>
                <Field label="C.P.">
                  <Input name="CodigoPostal" value={form.CodigoPostal} onChange={handleChange} />
                </Field>
              </Section>

              <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 8 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
                  <input type="checkbox" name="Activo" checked={form.Activo} onChange={handleChange} />
                  Empleado activo
                </label>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 24 }}>
                <button type="button" className="lmd-btn lmd-btn-outline" onClick={() => setShowModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="lmd-btn lmd-btn-primary" disabled={saving}>
                  {saving ? "Guardando..." : editId ? "Guardar cambios" : "Crear empleado"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
